import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ReviewsSection = ({ reviews = [], averageRating = 0, totalReviews = 0 }) => {
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [filterRating, setFilterRating] = useState(0);
  
  const displayedReviews = showAllReviews ? reviews : reviews?.slice(0, 3);
  const filteredReviews = filterRating > 0 
    ? displayedReviews?.filter(review => review?.rating === filterRating)
    : displayedReviews;

  const ratingDistribution = [5, 4, 3, 2, 1]?.map(rating => {
    const count = reviews?.filter(review => review?.rating === rating)?.length;
    const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
    return { rating, count, percentage };
  });

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const renderStars = (rating, size = 16) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={size}
        color={index < rating ? 'var(--color-warning)' : 'var(--color-muted-foreground)'}
        className={index < rating ? 'fill-current' : ''}
      />
    ));
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="font-heading font-semibold text-lg text-foreground mb-6">
        Reseñas y Valoraciones
      </h3>
      {/* Rating Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Overall Rating */}
        <div className="text-center lg:text-left">
          <div className="flex items-center justify-center lg:justify-start space-x-2 mb-2">
            <span className="text-3xl font-bold text-foreground">
              {averageRating?.toFixed(1)}
            </span>
            <div className="flex space-x-1">
              {renderStars(Math.round(averageRating), 20)}
            </div>
          </div>
          <p className="text-muted-foreground text-sm">
            Basado en {totalReviews} reseñas
          </p>
        </div>

        {/* Rating Distribution */}
        <div className="space-y-2">
          {ratingDistribution?.map(({ rating, count, percentage }) => (
            <div key={rating} className="flex items-center space-x-3">
              <span className="text-sm text-muted-foreground w-8">
                {rating}★
              </span>
              <div className="flex-1 bg-muted rounded-full h-2">
                <div
                  className="bg-warning rounded-full h-2 transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-sm text-muted-foreground w-8 text-right">
                {count}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Filter Options */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          variant={filterRating === 0 ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterRating(0)}
        >
          Todas
        </Button>
        {[5, 4, 3, 2, 1]?.map(rating => (
          <Button
            key={rating}
            variant={filterRating === rating ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterRating(rating)}
          >
            {rating}★
          </Button>
        ))}
      </div>
      {/* Reviews List */}
      <div className="space-y-4">
        {filteredReviews?.length > 0 ? (
          filteredReviews?.map((review) => (
            <div key={review?.id} className="border-b border-border last:border-b-0 pb-4 last:pb-0">
              <div className="flex items-start space-x-3">
                {/* User Avatar */}
                <div className="flex-shrink-0">
                  {review?.userAvatar ? (
                    <Image
                      src={review?.userAvatar}
                      alt={review?.userAvatarAlt}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                      {review?.userName?.charAt(0)?.toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Review Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-foreground text-sm">
                        {review?.userName}
                      </h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <div className="flex space-x-1">
                          {renderStars(review?.rating, 14)}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(review?.date)}
                        </span>
                      </div>
                    </div>
                    
                    {review?.isVerified && (
                      <div className="flex items-center space-x-1 bg-success/10 text-success px-2 py-1 rounded-md">
                        <Icon name="CheckCircle" size={12} />
                        <span className="text-xs font-medium">Verificada</span>
                      </div>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                    {review?.comment}
                  </p>

                  {/* Review Images */}
                  {review?.images && review?.images?.length > 0 && (
                    <div className="flex space-x-2 mb-2">
                      {review?.images?.slice(0, 3)?.map((image, index) => (
                        <div key={index} className="w-16 h-16 bg-muted rounded-md overflow-hidden">
                          <Image
                            src={image?.url}
                            alt={image?.alt}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                      {review?.images?.length > 3 && (
                        <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center">
                          <span className="text-xs text-muted-foreground">
                            +{review?.images?.length - 3}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Helpful Actions */}
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <button className="flex items-center space-x-1 hover:text-foreground transition-smooth">
                      <Icon name="ThumbsUp" size={12} />
                      <span>Útil ({review?.helpfulCount || 0})</span>
                    </button>
                    
                    {review?.businessResponse && (
                      <button className="flex items-center space-x-1 hover:text-foreground transition-smooth">
                        <Icon name="MessageCircle" size={12} />
                        <span>Respuesta del negocio</span>
                      </button>
                    )}
                  </div>

                  {/* Business Response */}
                  {review?.businessResponse && (
                    <div className="mt-3 bg-muted/50 p-3 rounded-md">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="Store" size={14} color="var(--color-primary)" />
                        <span className="text-sm font-medium text-foreground">
                          Respuesta del negocio
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(review?.businessResponse?.date)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {review?.businessResponse?.message}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <Icon name="MessageCircle" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-muted-foreground">
              {filterRating > 0 
                ? `No hay reseñas con ${filterRating} estrellas`
                : 'No hay reseñas disponibles'
              }
            </p>
          </div>
        )}
      </div>
      {/* Show More Button */}
      {reviews?.length > 3 && !showAllReviews && (
        <div className="text-center mt-6">
          <Button
            variant="outline"
            onClick={() => setShowAllReviews(true)}
          >
            Ver todas las reseñas ({reviews?.length})
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;